import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Play, Pause, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import { useProfile } from '@/hooks/useProfile';

interface VoiceMessagePlayerProps {
  audioUrl: string;
  isOwnMessage: boolean;
  senderLanguage?: string;
}

export const VoiceMessagePlayer = ({ audioUrl, isOwnMessage, senderLanguage }: VoiceMessagePlayerProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedAudioUrl, setTranslatedAudioUrl] = useState<string | null>(null);
  const [playingTranslated, setPlayingTranslated] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const translatedAudioRef = useRef<HTMLAudioElement | null>(null);
  const { toast } = useToast();
  const { t, language } = useLanguage();
  const { profile } = useProfile();

  // Match ChatScreen's pattern: profile setting first, then UI language, then 'en'
  const preferredLanguage = profile?.preferred_language || language || 'en';

  const handlePlayOriginal = async () => {
    console.log('[VoiceMessagePlayer] handlePlayOriginal called, audioUrl:', audioUrl);
    
    if (!audioRef.current) {
      console.log('[VoiceMessagePlayer] Creating new Audio element for original');
      audioRef.current = new Audio(audioUrl);
      audioRef.current.onended = () => {
        console.log('[VoiceMessagePlayer] Original audio ended');
        setIsPlaying(false);
      };
      audioRef.current.onerror = (e) => {
        console.error('[VoiceMessagePlayer] Original audio error:', e);
        setIsPlaying(false);
        toast({
          title: t('error'),
          description: 'Failed to play audio',
          variant: 'destructive',
        });
      };
      audioRef.current.oncanplaythrough = () => {
        console.log('[VoiceMessagePlayer] Original audio can play through');
      };
    }

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      // Stop translated audio if playing
      if (translatedAudioRef.current) {
        translatedAudioRef.current.pause();
        setPlayingTranslated(false);
      }
      try {
        console.log('[VoiceMessagePlayer] Attempting to play original audio');
        await audioRef.current.play();
        console.log('[VoiceMessagePlayer] Original audio playing');
        setIsPlaying(true);
      } catch (playErr) {
        console.error('[VoiceMessagePlayer] Play error:', playErr);
        toast({
          title: t('error'),
          description: 'Failed to play audio: ' + (playErr instanceof Error ? playErr.message : 'Unknown error'),
          variant: 'destructive',
        });
      }
    }
  };

  const handlePlayTranslated = async () => {
    const clearTranslatedCache = () => {
      if (translatedAudioUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(translatedAudioUrl);
      }
      translatedAudioRef.current = null;
      setTranslatedAudioUrl(null);
      setPlayingTranslated(false);
    };

    const base64ToAudioBlob = (base64: string, mimeType: string) => {
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);

      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }

      return new Blob([bytes.buffer], { type: mimeType });
    };

    // If we already have translated audio, just play it
    if (translatedAudioUrl && translatedAudioRef.current) {
      if (playingTranslated) {
        translatedAudioRef.current.pause();
        setPlayingTranslated(false);
      } else {
        // Stop original audio if playing
        if (audioRef.current) {
          audioRef.current.pause();
          setIsPlaying(false);
        }

        try {
          await translatedAudioRef.current.play();
          setPlayingTranslated(true);
        } catch (playErr) {
          console.error('[VoiceMessagePlayer] Cached translated audio play error:', playErr);
          clearTranslatedCache();
          toast({
            title: t('error'),
            description: 'Translated audio could not be played. Please tap again.',
            variant: 'destructive',
          });
        }
      }
      return;
    }

    // Pre-create audio element NOW (inside user gesture) to satisfy iOS autoplay policy
    const preAudio = new Audio();
    preAudio.preload = 'auto';

    // Fire-and-forget unlock: never block translation request on this step
    try {
      preAudio.src = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEAIlYAAESsAAACABAAZGF0YQQAAAAAAA==';
      preAudio.volume = 0;
      void preAudio
        .play()
        .then(() => {
          preAudio.pause();
          preAudio.currentTime = 0;
          preAudio.volume = 1;
          console.log('[VoiceMessagePlayer] Audio element unlocked successfully');
        })
        .catch((e) => {
          preAudio.volume = 1;
          console.log('[VoiceMessagePlayer] Pre-unlock silent play skipped:', e);
        });
    } catch (e) {
      preAudio.volume = 1;
      console.log('[VoiceMessagePlayer] Pre-unlock setup skipped:', e);
    }

    setIsTranslating(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const accessToken = session?.access_token;

      if (!accessToken) {
        throw new Error('Not authenticated. Please sign in again.');
      }

      const { data, error } = await supabase.functions.invoke('translate-audio', {
        headers: { Authorization: `Bearer ${accessToken}` },
        body: {
          audioUrl,
          targetLanguage: preferredLanguage,
          sourceLanguage: senderLanguage,
        },
      });

      if (error) {
        const ctx = (error as { context?: Response }).context;
        let serverError: string | undefined;
        try {
          const body = await ctx?.clone().json();
          serverError = body?.error;
        } catch {
          // response body wasn't JSON (or already consumed) — fall back below
        }
        console.error('[VoiceMessagePlayer] translate-audio invoke error:', {
          message: error.message,
          status: ctx?.status,
          serverError,
        });
        const status = ctx?.status ? ` (HTTP ${ctx.status})` : '';
        throw new Error(serverError ? `${serverError}${status}` : `${error.message || 'Translation failed'}${status}`);
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      if (!data?.audioContent) {
        throw new Error('No audio returned');
      }

      const translatedMimeType = typeof data?.mimeType === 'string' ? data.mimeType : 'audio/wav';
      const audioBlob = base64ToAudioBlob(data.audioContent, translatedMimeType);
      if (audioBlob.size === 0) {
        throw new Error('Translated audio is empty');
      }

      const objectUrl = URL.createObjectURL(audioBlob);

      if (translatedAudioUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(translatedAudioUrl);
      }
      setTranslatedAudioUrl(objectUrl);

      preAudio.src = objectUrl;
      preAudio.onended = () => {
        console.log('[VoiceMessagePlayer] Translated audio ended');
        setPlayingTranslated(false);
      };
      preAudio.onerror = (e) => {
        console.error('[VoiceMessagePlayer] Translated audio error:', e);
        setPlayingTranslated(false);
        clearTranslatedCache();
        toast({
          title: t('error'),
          description: 'Failed to play translated audio',
          variant: 'destructive',
        });
      };
      translatedAudioRef.current = preAudio;

      // Stop original audio if playing
      if (audioRef.current) {
        audioRef.current.pause();
        setIsPlaying(false);
      }

      console.log('[VoiceMessagePlayer] Attempting to play translated audio');
      try {
        await preAudio.play();
        console.log('[VoiceMessagePlayer] Translated audio playing');
        setPlayingTranslated(true);
      } catch (playErr) {
        console.error('[VoiceMessagePlayer] Translated audio play error:', playErr);
        clearTranslatedCache();
        toast({
          title: t('error'),
          description: 'Failed to play translated audio: ' + (playErr instanceof Error ? playErr.message : 'Unknown error'),
          variant: 'destructive',
        });
      }
    } catch (err) {
      console.error('Translation error:', err);
      toast({
        title: t('error'),
        description: err instanceof Error ? err.message : 'Failed to translate audio',
        variant: 'destructive',
      });
    } finally {
      setIsTranslating(false);
    }
  };


  return (
    <div className="flex flex-col gap-1">
      {/* Play Translated - shown on top for received messages */}
      {!isOwnMessage && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handlePlayTranslated}
          disabled={isTranslating}
          className="h-8 gap-2 justify-start text-xs w-full"
        >
          {isTranslating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : playingTranslated ? (
            <Pause className="h-4 w-4" />
          ) : (
            <Play className="h-4 w-4" />
          )}
          {isTranslating ? 'Translating...' : translatedAudioUrl ? (playingTranslated ? 'Pause Translated' : 'Play Translated') : 'Play Translated'}
        </Button>
      )}

      {/* Play Original - shown below for received messages, or as primary for own messages */}
      <Button
        variant="ghost"
        size="sm"
        onClick={handlePlayOriginal}
        className="h-8 gap-2 justify-start text-xs w-full"
      >
        {isPlaying ? (
          <Pause className="h-4 w-4" />
        ) : (
          <Play className="h-4 w-4" />
        )}
        {isPlaying ? 'Pause Original' : 'Play Original'}
      </Button>
    </div>
  );
};
